import { useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useRecipe } from "../api/queries";
import { SCROLLS_OFF, countsScrolls } from "../api/recipeQuery";
import "./pages.css";
import type { RecipeNode } from "../api/types";
import { useSession } from "../auth";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { NpcPriceInline } from "../components/NpcPriceInline";
import { Panel } from "../components/Panel";
import { ScanListDialog } from "../components/ScanListDialog";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { adena, signed, when } from "../format";
import { itemHref } from "../search/nav";
import type { CostMode } from "./recipeSort";

/** One material, and its children when it is craftable. Flattened to rows here
 *  rather than rendered as nested tables: a table inside a table breaks the
 *  column alignment that makes the costs readable down the page. */
function rows(node: RecipeNode, open: Set<string>, path = "", depth = 0): {
  node: RecipeNode; depth: number; key: string; expandable: boolean;
}[] {
  const key = `${path}/${node.name}`;
  const expandable = node.children.length > 0;
  const out = [{ node, depth, key, expandable }];
  if (expandable && open.has(key)) {
    for (const child of node.children) out.push(...rows(child, open, key, depth + 1));
  }
  return out;
}

/** The comparison that decided a node — or, when its craft reading has a hole,
 *  how big the hole is.
 *
 *  §5: a sum with a hole is not a sum. A node whose craft could not be
 *  completed has no craft figure to compare, so this column would otherwise be
 *  blank on exactly the rows that have something to say — and `missing`, which
 *  the API has always carried, would reach no reader at all. Dim and last in
 *  the row: a footnote, not an alarm. */
function comparison(node: RecipeNode): string {
  if (node.unit_buy !== null && node.unit_craft !== null) {
    return `buy ${adena(Math.round(node.unit_buy))} · craft ${adena(
      Math.round(node.unit_craft),
    )} → ${node.source}`;
  }
  if (node.source === "craft") return "craft (nobody is selling one)";
  // children.length is the recipe's own line count: the node carries the
  // materials of the recipe it names, priced or not.
  if (node.missing > 0) return `craft: ${node.missing} of ${node.children.length} unpriced`;
  return "";
}

export function Recipe() {
  const { id = "" } = useParams();
  const recipeId = Number(id);
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const mode: CostMode = params.get("mode") === "buy" ? "buy" : "craft";
  // Checked by default: a craft consumes the recipe on this server, so the
  // scroll is a material and counting it is the accurate reading. Unlike the
  // mode toggle this one is a refetch — it changes which materials the tree
  // and both totals are built from — so it rides in the query key.
  const scrolls = countsScrolls(params);
  const detail = useRecipe(recipeId, scrolls);
  // uploader and owner, never a viewer — absent rather than disabled, the rule
  // the item page's own price panel follows.
  const canPrice = me?.role === "uploader" || me?.role === "owner";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  // The first level is the decision; the rest is the evidence. Depth 1 opens on
  // load and everything below it stays shut until asked for.
  const [open, setOpen] = useState<Set<string>>(new Set());
  // The materials as one line to paste into the market analyzer. State here
  // rather than in the dialog because the strip's button is what opens it, and
  // a <dialog> that owned its own openness would have no way to be opened from
  // outside itself.
  const [scanOpen, setScanOpen] = useState(false);

  // The first level opens on load; anything deeper waits to be asked for.
  // Keyed on the recipe id so navigating between recipes re-seeds it rather
  // than carrying one recipe's open rows onto another.
  //
  // Deviation from the brief: seeding the *children's* keys here (as the brief's
  // Step 2 literally has it) never opens the root's own key, and rows() only
  // descends into a node's children when that node's own key is in `open` — so
  // the root's materials (depth 1) would never render at all, for any recipe.
  // Seeding the root's key instead reveals depth 1 (its materials) while their
  // own keys stay unseeded, so depth 2 stays shut until a reader asks for it —
  // matching "depth 1 is expanded and deeper levels are not".
  const [seeded, setSeeded] = useState<number | null>(null);
  if (detail.data && seeded !== recipeId) {
    setSeeded(recipeId);
    setOpen(new Set([`/${detail.data.tree.name}`]));
  }

  // Not Number.isNaN: Number("") is 0 and Number("3.5") is 3.5, and neither is
  // a recipe id — the same guard the item page carries.
  if (!Number.isInteger(recipeId) || recipeId <= 0) {
    return (
      <>
        <TitleBand title="Recipe" />
        <ErrorBand message="That is not a recipe." hint="Open one from the recipe list." />
      </>
    );
  }
  if (detail.isLoading) return <><TitleBand title="Recipe" /><Skeleton rows={8} /></>;
  if (detail.isError || !detail.data) {
    return (
      <>
        <TitleBand title="Recipe" />
        <ErrorBand
          message="Could not load this recipe."
          hint="It may not exist in the catalogue. Back to the recipe list."
        />
      </>
    );
  }

  const data = detail.data;
  const cost = mode === "buy" ? data.cost_buy : data.cost_craft;
  const priced = mode === "buy" ? data.priced_buy : data.priced_craft;
  // The bid, not the ask, and the same rule the catalogue's Margin column is
  // struck by server-side: an ask is a price a seller hopes for, a bid is money
  // a buyer has already committed to. This page has to agree with that column
  // about the same recipe — one word labelling two different numbers is how a
  // reader stops trusting either — so when `Pricer.row` changes sides, so does
  // this. No fallback to the ask when nobody is bidding: the figure is absent.
  const margin = data.market.bid === null || cost === null ? null : data.market.bid - cost;
  const tree = rows(data.tree, open);

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <>
      <TitleBand
        title={data.count > 1 ? `${data.craft_item} ×${data.count}` : data.craft_item}
        note={`${data.grade} · craft level ${data.craft_level} · ${data.success}% · ${data.mp} MP${
          data.crafter_level === null ? "" : ` · crafter level ${data.crafter_level}`
        }`}
      />

      <Panel primary>
        {/* Three groups, separated by rules: which cost to read, what to count,
            and what to go and look at. The second is not a variant of the first
            — it changes the materials both costs are summed from — and the
            third changes nothing on the page at all. */}
        <div className="strip">
          <div className="group">
            <button
              type="button"
              aria-pressed={mode === "buy"}
              onClick={() => set("mode", mode === "buy" ? "" : "buy")}
            >
              market prices only
            </button>
            <button type="button" aria-pressed={mode === "craft"} onClick={() => set("mode", "")}>
              prices + crafting
            </button>
          </div>
          <div className="group">
            <label className="check">
              <input
                type="checkbox"
                checked={scrolls}
                onChange={() => set("scrolls", scrolls ? SCROLLS_OFF : "")}
              />
              <span>count recipe scrolls</span>
            </label>
          </div>
          {/* Not a URL parameter like the two beside it: this opens a dialog
              over the page rather than changing what the page says, and a
              recipe link that reopened a modal for whoever it was pasted to
              would be sharing a gesture instead of a reading. */}
          <div className="group">
            <button type="button" onClick={() => setScanOpen(true)}>
              scan list
            </button>
          </div>
        </div>

        <dl className="cost-summary">
          <div>
            <dt>Cost</dt>
            <dd className="gold">{adena(cost)}</dd>
          </div>
          <div>
            <dt>Materials priced</dt>
            <dd className={priced === data.lines ? "" : "dim"}>{priced} of {data.lines}</dd>
          </div>
          <div>
            <dt>Sells for</dt>
            <dd className="sell">{adena(data.market.ask)}</dd>
          </div>
          <div>
            <dt>Buys for</dt>
            <dd className="buy">{adena(data.market.bid)}</dd>
          </div>
          <div>
            <dt>Margin</dt>
            <dd className={margin === null ? "dim" : margin < 0 ? "buy" : "sell"}>
              {signed(margin)}
            </dd>
          </div>
          <div>
            <dt>Crafter fee</dt>
            <dd className="dim">{adena(data.market.fee)}</dd>
          </div>
          <div>
            <dt>Last seen</dt>
            <dd className="dim">
              {data.market.observed_at === null ? "—" : when(data.market.observed_at)}
            </dd>
          </div>
        </dl>
      </Panel>

      {/* The Cost column stacks a parent and its children, and their costs are
          at different scales: §7 defines a node's count as "per one craft of
          the parent", so Cord ×4 → 200 sits directly above the Steel and
          Thread of one Cord batch, not of four. The data contract is right —
          a per-craft-of-parent count composes up the tree — so the column says
          what it means rather than being quietly rescaled. */}
      <TitleBand
        title="Materials"
        note={`${
          mode === "buy" ? "every material bought" : "cheaper of buying and crafting"
        } · each cost is for the quantity its own parent needs`}
      />
      <Panel>
        {tree.length <= 1 ? (
          <Empty>This recipe lists no materials.</Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Material</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit</th>
                  <th className="num">Cost</th>
                  <th>Cheaper to</th>
                </tr>
              </thead>
              <tbody>
                {tree.slice(1).map(({ node, depth, key, expandable }) => {
                  const unit = mode === "buy" ? node.unit_buy : node.unit;
                  const line = mode === "buy" ? node.cost_buy : node.cost_craft;
                  // Whether the rows below this one are the branch the active
                  // total did *not* take: in prices-only mode nothing is ever
                  // crafted, so every open subtree is the alternative; in
                  // prices+crafting only the nodes that won on their own ask.
                  const alternative =
                    expandable && open.has(key) && (mode === "buy" || node.source === "buy");
                  return (
                    <tr key={key}>
                      <td style={{ "--depth": depth } as CSSProperties} className="tree-cell">
                        {expandable ? (
                          <button
                            type="button"
                            className="disclose"
                            aria-expanded={open.has(key)}
                            onClick={() => toggle(key)}
                          >
                            {open.has(key) ? "−" : "+"}
                          </button>
                        ) : (
                          <span className="disclose-spacer" />
                        )}
                        {node.item_id === null ? (
                          <span>{node.name}</span>
                        ) : (
                          <Link to={itemHref(node.item_id, node.name)}>{node.name}</Link>
                        )}
                        {/* Never scanned under any alias — a fact about our data,
                            not about the market, and the two are different. */}
                        {node.resolved ? null : <span className="dim"> · not scanned</span>}
                        {/* The only place this material can be priced at all:
                            with no scan there is no item row, no id, and so no
                            item page to carry the panel that would do it.
                            Adena is `resolved` in the cost engine, so the one
                            row that is never a purchase never offers it. */}
                        {canPrice && !node.resolved ? <NpcPriceInline name={node.name} /> : null}
                        {/* The chosen unit price came from a vendor's price
                            list, not an observed trade — a different kind of
                            number from everything else in this tree. */}
                        {node.unit_is_npc ? <span className="dim"> · NPC</span> : null}
                        {/* The total took this node's ask, not one figure below
                            it. An unlabelled subtree of costs the total never
                            spent reads as though it had. */}
                        {alternative ? (
                          <span className="dim"> · below, if crafted instead</span>
                        ) : null}
                      </td>
                      <td className="num dim">{node.count}</td>
                      <td className="num">{unit === null ? "—" : adena(Math.round(unit))}</td>
                      <td className="num adena">{adena(line)}</td>
                      <td className="dim">{comparison(node)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <TitleBand title="The recipe itself" note="the scroll this recipe is taught by" />
      <Panel>
        {data.scroll.item_id === null ? (
          <>
            <Empty>{data.name} has never been scanned.</Empty>
            {/* A scroll a vendor sells and no player has ever listed is the
                same hole as an unscanned material, in the one panel the tree
                above cannot show it in. */}
            {canPrice ? <NpcPriceInline name={data.name} /> : null}
          </>
        ) : (
          <p className="scan-meta">
            <Link to={itemHref(data.scroll.item_id, data.name)}>{data.name}</Link>
            {" · asks "}
            <span className="sell">{adena(data.scroll.ask)}</span>
          </p>
        )}
        {/* Whether the cost above spent this scroll, said where that cost is
            read. On this server a craft consumes the recipe, which is why 610
            of the 995 catalogue entries list their own scroll as a material
            and why the checkbox is on by default; turned off, that material is
            still in the catalogue and has to be accounted for rather than
            silently dropped. One sentence in one span: .scan-meta is a flex
            container with a gap. */}
        {data.lists_own_scroll ? (
          <p className="scan-meta">
            <span>
              {scrolls
                ? "This craft consumes the scroll, so it is one of the materials above and part of the cost."
                : "The catalogue lists this scroll as a material; it is not counted here."}
            </span>
          </p>
        ) : null}
      </Panel>

      {/* Last in the tree and not beside the button that opens it: a modal is
          in the browser's top layer wherever it is written, and putting it at
          the foot keeps the strip above reading as three controls. */}
      <ScanListDialog open={scanOpen} onClose={() => setScanOpen(false)} detail={data} />
    </>
  );
}
